/**
 * PaymentTable component for displaying payment schedule
 * Constitutional requirement: React with regular JavaScript, console logging
 */

import React, { useState, useMemo } from 'react'
import { 
  BarChart3, 
  Lightbulb, 
  Calendar, 
  DollarSign, 
  ChevronsLeft, 
  ChevronLeft, 
  ChevronRight, 
  ChevronsRight,
  CreditCard, 
  Calendar1, 
  CheckCheck 
} from 'lucide-react'

const PaymentTable = ({ paymentSchedule = [], summary = {} }) => {
  console.log('🏗️ Rendering PaymentTable with', paymentSchedule.length, 'payments')
  console.log('📊 Summary data:', summary)

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(12) // Show 12 months per page (1 year)
  const [showAllRows, setShowAllRows] = useState(true)

  // Calculate pagination
  const totalPages = Math.ceil(paymentSchedule.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage

  // Get current page data
  const currentPageData = useMemo(() => {
    if (showAllRows) {
      //console.log('📋 Showing all payment rows')
      return paymentSchedule
    } else {
      const pageData = paymentSchedule.slice(startIndex, endIndex)
      console.log(`📋 Showing page ${currentPage} of ${totalPages} (${pageData.length} rows)`)
      return pageData
    }
  }, [paymentSchedule, currentPage, itemsPerPage, showAllRows, startIndex, endIndex, totalPages])

  // Handle page change
  const handlePageChange = (page) => {
    console.log(`📄 Changing to page ${page}`)
    setCurrentPage(page)
  }

  // Handle items per page change
  const handleItemsPerPageChange = (items) => {
    console.log(`📏 Changing items per page to ${items}`)
    setItemsPerPage(items)
    setCurrentPage(1) // Reset to first page
  }

  // Toggle show all rows
  const handleShowAllToggle = () => {
    console.log(`👁️ Toggling show all rows: ${!showAllRows}`)
    setShowAllRows(!showAllRows)
    setCurrentPage(1) // Reset to first page
  }

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount)
  }

  // Format month display (show year breaks)
  const formatMonth = (month) => {
    const years = Math.floor((month - 1) / 12)
    const monthInYear = ((month - 1) % 12) + 1
    
    if (years === 0) {
      return `Month ${month}`
    } else {
      return `Year ${years + 1}, Month ${monthInYear}`
    }
  }

  if (!paymentSchedule || paymentSchedule.length === 0) {
    console.log('📭 No payment schedule data to display')
    return (
      <div className="payment-table-container">
        <div className="no-data">
          <h3>
            <BarChart3 size={20} color="#ff6b35" style={{display: 'inline', marginRight: '8px'}} />
            Payment Schedule
          </h3>
          <p>
            <Lightbulb size={16} color="#ff6b35" style={{display: 'inline', marginRight: '4px'}} />
            Enter your debt details and calculate to see the payment schedule.
          </p>
        </div>
      </div>
    )
  }

  console.log('🎨 Rendering PaymentTable with data')

  return (
    <div className="payment-table-container">
      
      {/* Table Header */}
      <div className="table-header">
        <h3>
          <BarChart3 size={20} color="#ff6b35" style={{display: 'inline', marginRight: '8px'}} />
          Payment Schedule
        </h3>
        <div className="table-summary">
          <span className="summary-item">
            <Calendar size={16} color="#ff6b35" style={{display: 'inline', marginRight: '4px'}} />
            <strong>{summary.totalMonths || paymentSchedule.length}</strong> months
          </span>
          <span className="summary-item">
            <DollarSign size={16} color="#ff6b35" style={{display: 'inline', marginRight: '4px'}} />
            <strong>{formatCurrency(summary.totalInterest || 0)}</strong> total interest
          </span>
          <span className="summary-item">
            <CreditCard size={16} color="#ff6b35" style={{display: 'inline', marginRight: '4px'}} />
            <strong>{formatCurrency(summary.totalPayments || 0)}</strong> total payments
          </span>
        </div>
      </div>

      {/* Table Controls */}
      <div className="table-controls">
        <div className="pagination-controls">
          <label>
            Rows per page:
            <select 
              value={itemsPerPage} 
              onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
              disabled={showAllRows}
            >
              <option value={12}>12 (1 year)</option>
              <option value={24}>24 (2 years)</option>
              <option value={36}>36 (3 years)</option>
              <option value={60}>60 (5 years)</option>
            </select>
          </label>
          
          <label className="show-all-toggle">
            <input
              type="checkbox"
              checked={showAllRows}
              onChange={handleShowAllToggle}
            />
            Show all rows
          </label>
        </div>

        {!showAllRows && totalPages > 1 && (
          <div className="pagination">
            <div className="pagination-left">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="page-button"
              >
                <ChevronLeft size={16} color="#ff6b35" />
                Prev
              </button>
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                className="page-button"
              >
                <ChevronsLeft size={16} color="#ff6b35" />
                First
              </button>
            </div>
            
            <span className="page-info">
              Page {currentPage} of {totalPages}
            </span>
            
            <div className="pagination-right">
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="page-button"
              >
                Next
                <ChevronRight size={16} color="#ff6b35" />
              </button>
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                className="page-button"
              >
                Last
                <ChevronsRight size={16} color="#ff6b35" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Payment Table */}
      <div className="table-wrapper">
        <table className="payment-table">
          <thead>
            <tr>
              <th>
                {/* <Calendar size={16} color="#ff6b35" style={{display: 'inline', marginRight: '4px'}} /> */}
                Payment
              </th>
              <th>
                {/* <DollarSign size={16} color="#ff6b35" style={{display: 'inline', marginRight: '4px'}} /> */}
                Starting Balance
              </th>
              <th>Payment Amount</th>
              <th>
                {/* <BarChart3 size={16} color="#ff6b35" style={{display: 'inline', marginRight: '4px'}} /> */}
                Interest Charge
              </th>
              <th>Principal Payment</th>
              <th>Ending Balance</th>
            </tr>
          </thead>
          <tbody>
            {currentPageData.map((payment, index) => {
              const isYearEnd = payment.month % 12 === 0
              const isLastPayment = payment.endingBalance <= 0.01
              
              return (
                <tr 
                  key={payment.month}
                  className={`
                    ${isYearEnd ? 'year-end' : ''}
                    ${isLastPayment ? 'final-payment' : ''}
                  `}
                >
                  <td className="month-cell">
                    {formatMonth(payment.month)}
                    {isYearEnd && <span className="year-marker">
                      {/* 🎯 */}
                      <Calendar1 size={16} color="#ff6b35" style={{display: 'inline', marginRight: '4px'}} />
                    </span>}
                    {isLastPayment && <span className="final-marker">
                      {/* ✅ */}
                      <CheckCheck size={16} color="#007220ff" style={{display: 'inline', marginRight: '4px'}} />
                    </span>}
                  </td>
                  <td className="currency">{formatCurrency(payment.startingBalance)}</td>
                  <td className="currency payment-amount">{formatCurrency(payment.payment)}</td>
                  <td className="currency interest-charge">{formatCurrency(payment.interestCharge)}</td>
                  <td className="currency principal-payment">{formatCurrency(payment.principalPayment)}</td>
                  <td className="currency ending-balance">
                    {formatCurrency(payment.endingBalance)}
                    {isLastPayment && <span className="paid-off">PAID OFF!</span>}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Table Footer Summary */}
      {showAllRows && paymentSchedule.length > 0 && (
        <div className="table-footer">
          <div className="footer-summary">
            <div className="summary-row">
              <span className="summary-label">
                <BarChart3 size={16} color="#ff6b35" style={{display: 'inline', marginRight: '4px'}} />
                Totals:
              </span>
              <span className="summary-value">
                {formatCurrency(summary.totalPayments || 0)} total payments
              </span>
              <span className="summary-value">
                <BarChart3 size={12} color="#ff6b35" style={{display: 'inline', marginRight: '4px'}} />
                {formatCurrency(summary.totalInterest || 0)} total interest
              </span>
              <span className="summary-value">
                {formatCurrency(summary.totalPrincipal || 0)} principal paid
              </span>
            </div>
            {summary.payoffDate && (
              <div className="payoff-date">
                🎉 <strong>Paid off by:</strong> {new Date(summary.payoffDate).toLocaleDateString()}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Legend */}
      {/* <div className="table-legend">
        <h4>Legend:</h4>
        <div className="legend-items">
          <span className="legend-item">Year End</span>
          <span className="legend-item">Final Payment</span>
          <span className="legend-item interest-legend">
            <BarChart3 size={12} color="#ff6b35" style={{display: 'inline', marginRight: '4px'}} />
            Interest charges in grey
          </span>
          <span className="legend-item principal-legend">Principal payments in orange</span>
        </div>
      </div> */}

    </div>
  )
}

export default PaymentTable
